// Apollo
import { useQuery } from "@apollo/client";

// Design System
import { Button, DatePicker, Modal, SelectDropdown } from "@toddle-edu/ds-web";
import { DownloadOutlined } from "@toddle-edu/ds-icons";

// External
import _ from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

// Utils
import { compose } from "lib/apolloClient";
import { getOrganizationCurriculumPrograms } from "modules/CommonSelectors";

// Components
import SkeletonLoader from "UIComponents/SkeletonLoader";

// Relative - Queries & Helpers
import {
  getSchoolTemplatesQuery,
  getStudentsQuery,
} from "../../modules/queries";
import {
  isQueryLoading,
  getTemplateOptions,
  formatSchoolLabels,
} from "../../modules/helpers";

// Relative - Hooks
import { useDownloadTranscripts } from "../../hooks";

// Relative - Components
import StudentsList from "../StudentsList";

// CSS
import classes from "./BulkDownload.scss";

const BulkDownload = ({
  onClose,
  curriculumPrograms,
  schoolIds,
  templates: accountTemplates,
  studentIds,
  studentIdToSchoolIdMap,
  resetSelection,
  testId,
}) => {
  const [selectedStudentIds, setSelectedStudentIds] = useState(
    () => new Set(studentIds)
  );
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [issueDate, setIssueDate] = useState(moment());
  const { t } = useTranslation();

  const schoolIdToSchoolDataMap = useMemo(() => {
    return _.chain(curriculumPrograms).keyBy("id").value();
  }, [curriculumPrograms]);

  const {
    data: templatesData,
    networkStatus: templatesNetworkStatus,
  } = useQuery(getSchoolTemplatesQuery, {
    variables: { id: _.first(schoolIds) },
    fetchPolicy: "cache-and-network",
    skip: _.size(schoolIds) > 1,
  });

  const { data: studentsData, networkStatus: studentsNetworkStatus } = useQuery(
    getStudentsQuery,
    {
      variables: { ids: studentIds },
      fetchPolicy: "cache-and-network",
      skip: _.isEmpty(studentIds),
    }
  );

  const schoolLabels = useMemo(
    () => formatSchoolLabels(schoolIds, schoolIdToSchoolDataMap),
    [schoolIdToSchoolDataMap, schoolIds]
  );

  const isTemplatesLoading = isQueryLoading(
    templatesData,
    templatesNetworkStatus
  );

  const options = useMemo(() => {
    if (_.size(schoolIds) > 1) {
      return getTemplateOptions({
        templates: accountTemplates,
        hasEdgesStructure: false,
        t,
      });
    }

    const templates = _.get(
      templatesData,
      "node.transcriptTemplates.edges",
      []
    );
    return getTemplateOptions({
      templates,
      t,
    });
  }, [accountTemplates, schoolIds, templatesData, t]);

  useEffect(() => {
    setSelectedTemplate(prev => {
      const prevId = _.get(prev, "value");
      if (prevId) {
        const isPrevIdPresent = _.some(options, { value: prevId });
        if (isPrevIdPresent) return prev;
      }

      return _.first(options);
    });
  }, [options]);

  const handleTemplateSelect = useCallback(option => {
    setSelectedTemplate(option);
  }, []);

  const downloadTranscripts = useDownloadTranscripts();

  const handleDownload = useCallback(() => {
    downloadTranscripts({
      studentIds: Array.from(selectedStudentIds),
      studentIdToSchoolIdMap,
      issueDate,
      templateId: selectedTemplate?.value,
      studentsData,
      schoolIdToSchoolDataMap,
      selectedTemplateName: selectedTemplate?.label,
    });
    if (resetSelection) resetSelection();
    onClose();
  }, [
    downloadTranscripts,
    issueDate,
    schoolIdToSchoolDataMap,
    selectedStudentIds,
    selectedTemplate,
    studentIdToSchoolIdMap,
    studentsData,
    onClose,
    resetSelection,
  ]);

  return (
    <Modal isOpen onClose={onClose} width="840px" height="640px">
      <Modal.Header
        testId={`${testId}-bulkDownload`}
        title={t("common:download_with_label", {
          label: t("transcript:transcript"),
        })}
        subtitle={t("transcript:for_students_of_label", {
          label: schoolLabels,
        })}
        hasSeparator
      />
      <Modal.Body isScrollable>
        <div className={classes.body}>
          <div className={classes.fields}>
            <div className={classes.field}>
              <div className="text-label">
                {t("transcript:transcript_template")}
              </div>

              {isTemplatesLoading ? (
                <SkeletonLoader height="36px" width="100%" />
              ) : (
                <SelectDropdown
                  options={options}
                  value={selectedTemplate}
                  onChange={handleTemplateSelect}
                  placeholder={t("common:select_with_label", {
                    label: t("common:template"),
                  })}
                  isClearable={false}
                  testId={`${testId}-bulkDownload-templateDropdown`}
                  maxMenuHeight={188}
                />
              )}
            </div>
            <div className={classes.field}>
              <div className="text-label">{t("common:issue_date")}</div>
              <DatePicker
                placeholder={t("common:issue_date")}
                format="DD MMM YYYY"
                value={_.isEmpty(issueDate) ? undefined : issueDate}
                onChange={setIssueDate}
                testId={`${testId}-bulkDownload-issueDatePicker`}
              />
            </div>
          </div>

          <div className={classes.separator} />

          <StudentsList
            selectedStudentIds={selectedStudentIds}
            setSelectedStudentIds={setSelectedStudentIds}
            data={studentsData}
            networkStatus={studentsNetworkStatus}
            studentIdToSchoolIdMap={studentIdToSchoolIdMap}
            schoolIdToSchoolDataMap={schoolIdToSchoolDataMap}
            schoolIds={schoolIds}
            studentIds={studentIds}
            testId={`${testId}-bulkDownload`}
          />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className={classes.footer}>
          <div className="text-label">
            {t("common:student_selected_with_count", {
              count: selectedStudentIds.size,
            })}
          </div>

          <div className={classes.footerRight}>
            <Button
              type="outlined"
              variant="neutral"
              onClick={onClose}
              testId={`${testId}-bulkDownload-cancel`}
            >
              {t("common:cancel")}
            </Button>

            <Button
              variant="progressive"
              icon={<DownloadOutlined />}
              testId={`${testId}-bulkDownload-download`}
              disabled={
                isTemplatesLoading ||
                !selectedTemplate ||
                _.isEmpty(selectedStudentIds)
              }
              onClick={handleDownload}
            >
              {t("common:download")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

BulkDownload.propTypes = {
  onClose: PropTypes.func.isRequired,
  curriculumPrograms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      logo: PropTypes.string,
    })
  ).isRequired,
  schoolIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
  studentIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  studentIdToSchoolIdMap: PropTypes.objectOf(PropTypes.string).isRequired,
  resetSelection: PropTypes.func,
  testId: PropTypes.string,
};

BulkDownload.defaultProps = {
  resetSelection: null,
  testId: "",
};

const mapStateToProps = state => ({
  curriculumPrograms: getOrganizationCurriculumPrograms(state),
});

export default compose(connect(mapStateToProps), memo)(BulkDownload);
